/**
 * Sentry + PostHog initialization.
 *
 * Both are opt-in by env var. When the env var is missing (dev, or until the
 * operator provisions the keys), init is a no-op — nothing is loaded, no
 * network traffic, no console noise.
 */

import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

interface ObservabilityEnv {
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  VITE_POSTHOG_KEY?: string;
  VITE_POSTHOG_HOST?: string;
  MODE?: string;
}

let initialized = false;

export function initObservability(): void {
  if (initialized) return;
  initialized = true;

  const env = import.meta.env as unknown as ObservabilityEnv;

  if (env.VITE_SENTRY_DSN) {
    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,
      environment: env.VITE_SENTRY_ENVIRONMENT ?? env.MODE ?? "production",
      // Keep instrumentation minimal — no replay, no profiling, no spans
      // beyond uncaught errors. This is "Agnes-Hawk-pattern" support
      // diagnostics, not full APM.
      tracesSampleRate: 0,
      sampleRate: 1.0,
      // Filter out the buyer's email and any access-token-like strings
      // from breadcrumbs and event payloads.
      beforeSend(event) {
        return scrubPii(event);
      },
      beforeBreadcrumb(crumb) {
        if (crumb.data?.url && typeof crumb.data.url === "string") {
          crumb.data.url = scrubUrl(crumb.data.url);
        }
        return crumb;
      },
    });
  }

  if (env.VITE_POSTHOG_KEY) {
    posthog.init(env.VITE_POSTHOG_KEY, {
      api_host: env.VITE_POSTHOG_HOST ?? "https://app.posthog.com",
      person_profiles: "identified_only",
      // Capture autoclicks + pageviews; disable session recording (we don't
      // want to record buyer's screens, even with masking).
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: true,
      // Don't capture the actual input values (so OAuth client IDs, license
      // keys, API keys never make it into PostHog).
      mask_all_text: false,
      mask_personal_data_properties: true,
    });
  }
}

/**
 * Identify the signed-in user to both Sentry and PostHog. Call after the
 * session is hydrated, and on every login.
 */
export function identifyUser(email: string, tier?: string): void {
  if (!initialized) return;
  try {
    Sentry.setUser({ email });
    Sentry.setTag("tier", tier ?? "unknown");
  } catch {
    /* ignore */
  }
  try {
    posthog.identify(email, { tier });
  } catch {
    /* ignore */
  }
}

export function clearUser(): void {
  if (!initialized) return;
  try {
    Sentry.setUser(null);
  } catch {
    /* ignore */
  }
  try {
    posthog.reset();
  } catch {
    /* ignore */
  }
}

/* ----------------------------------------------- PII scrub */

const TOKEN_RE = /(ya29\.|EAA[A-Za-z0-9_-]{50,}|sk_(?:test|live)_[A-Za-z0-9]+|sentry_key=|AKIA[0-9A-Z]{16})/g;

function scrubString(s: string): string {
  return s.replace(TOKEN_RE, "[redacted]");
}

function scrubUrl(url: string): string {
  try {
    const u = new URL(url);
    for (const k of ["access_token", "refresh_token", "id_token", "code", "state"]) {
      if (u.searchParams.has(k)) u.searchParams.set(k, "[redacted]");
    }
    return u.toString();
  } catch {
    return scrubString(url);
  }
}

function scrubPii<T extends Sentry.ErrorEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  if (typeof event.message === "string") {
    event.message = scrubString(event.message);
  }
  return event;
}
